import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBlogs } from '../../hooks/useBlogs';
import { blogsService } from '../../services/dataService';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, Edit2, Trash2, Eye, EyeOff, Star } from 'lucide-react';
import { format } from 'date-fns';

const AdminBlogs = () => {
  const navigate = useNavigate();
  const { blogs, loading, error, refetch } = useBlogs(false); // Get all blogs, including drafts
  const [deleting, setDeleting] = useState(null);

  const handleDelete = async (id, title) => {
    if (window.confirm(`Delete "${title}"? This cannot be undone.`)) {
      setDeleting(id);
      try {
        const result = await blogsService.delete(id);
        if (result.success) {
          toast.success('Blog deleted successfully');
          refetch();
        } else {
          toast.error('Failed to delete blog');
        }
      } catch (error) {
        toast.error('Error deleting blog');
      } finally {
        setDeleting(null);
      }
    }
  };

  const togglePublish = async (blog) => {
    try {
      const result = await blogsService.update(blog.id, {
        published: !blog.published
      });
      
      if (result.success) {
        toast.success(blog.published ? 'Blog unpublished' : 'Blog published!');
        refetch();
      } else {
        toast.error('Failed to update blog');
      }
    } catch (error) {
      toast.error('Error updating blog');
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/adminaccess/dashboard')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <button
            onClick={() => navigate('/adminaccess/writings/new')}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>New Blog Post</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-light mb-2">Manage Blog Posts</h1>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="p-4 rounded-lg bg-white/5 border border-white/10">
            <div className="text-white/40 text-sm mb-1">Total Posts</div>
            <div className="text-2xl font-light">{blogs.length}</div>
          </div>
          <div className="p-4 rounded-lg bg-white/5 border border-white/10">
            <div className="text-white/40 text-sm mb-1">Published</div>
            <div className="text-2xl font-light text-green-400">
              {blogs.filter(b => b.published).length}
            </div>
          </div>
          <div className="p-4 rounded-lg bg-white/5 border border-white/10">
            <div className="text-white/40 text-sm mb-1">Drafts</div>
            <div className="text-2xl font-light text-yellow-400">
              {blogs.filter(b => !b.published).length}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="p-8 rounded-lg border border-white/10 bg-white/5 text-center">
            <div className="inline-block w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
            <p className="text-white/60">Loading writings...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-8 rounded-lg border border-red-500/20 bg-red-500/5 text-center">
            <p className="text-red-400">Error: {error}</p>
          </div>
        )}

        {/* Blogs List */}
        {!loading && !error && (
          <div className="space-y-4">
            {blogs.length === 0 ? (
              <div className="p-12 rounded-lg border border-white/10 bg-white/5 text-center">
                <p className="text-white/40 mb-4">No blog posts yet</p>
                <button
                  onClick={() => navigate('/adminaccess/writings/new')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Write Your First Post</span>
                </button>
              </div>
            ) : (
              blogs.map((blog) => (
                <div
                  key={blog.id}
                  className="p-5 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-all"
                >
                  <div className="flex items-start justify-between">
                    {/* Blog Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-medium">{blog.title}</h3>
                        {blog.featured && (
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        )}
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            blog.published
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}
                        >
                          {blog.published ? 'Published' : 'Draft'}
                        </span>
                      </div>

                      <p className="text-sm text-white/60 mb-3 line-clamp-2">
                        {blog.description}
                      </p>

                      <div className="flex items-center gap-4 text-xs text-white/40">
                        <span>{formatDate(blog.date)}</span>
                        <span>•</span>
                        <span>{blog.readTime}</span>
                        {blog.tags && blog.tags.length > 0 && (
                          <>
                            <span>•</span>
                            <div className="flex gap-1">
                              {blog.tags.slice(0, 3).map((tag, i) => (
                                <span key={i} className="px-2 py-0.5 bg-white/10 rounded">
                                  {tag}
                                </span>
                              ))}
                              {blog.tags.length > 3 && (
                                <span className="px-2 py-0.5">+{blog.tags.length - 3}</span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => togglePublish(blog)}
                        className={`p-2 rounded-lg transition-colors ${
                          blog.published
                            ? 'text-yellow-400 hover:bg-yellow-500/10'
                            : 'text-green-400 hover:bg-green-500/10'
                        }`}
                        title={blog.published ? 'Unpublish' : 'Publish'}
                      >
                        {blog.published ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                      
                      <button
                        onClick={() => navigate(`/adminaccess/writings/edit/${blog.id}`)}
                        className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => handleDelete(blog.id, blog.title)}
                        disabled={deleting === blog.id}
                        className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBlogs;